/** Loads one position's complete ranked group and routes its reorder writes. */
import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { WatchlistMoveDestination } from "@/features/scouting/components/molecules/WatchlistOrderControls/WatchlistOrderControlsContainer";
import usePositionTargetRows from "@/features/scouting/hooks/usePositionTargetRows";
import useSlotNaturalPositionIds from "@/features/scouting/hooks/useSlotNaturalPositionIds";
import {
  type SlotOrderController,
  type SlotOrderRow,
  watchlistSlotOrder,
} from "@/features/scouting/hooks/slotOrderController";
import {
  moveVisiblePlayer,
  type WatchlistPreferenceOrderController,
} from "@/features/scouting/hooks/useWatchlistPreferenceOrder";
import type { WeakSlotTargetPool } from "@/features/scouting/hooks/useWeakSlotTargetCounts";
import { appendPitchPage } from "@/features/scouting/utils/appendPitchPage";
import {
  type TacticalSlot,
  useScoutingGetWatchlistPitchQuery,
  type WatchlistPitchFilterInput,
} from "@/generated/graphql";

import type { PositionPopoutRowAction } from "@/features/scouting/components/molecules/WatchlistPositionPopoutRow";

import type { PitchSaveStatusLineProps } from "@/features/scouting/components/molecules/PitchSaveStatusLine";

import WatchlistPositionPopoutView, {
  type PositionPopoutCopy,
  type PositionPopoutOpening,
} from "./WatchlistPositionPopoutView";

/** Backend-enforced maximum page size for Pitch connections. */
const GROUP_PAGE_SIZE = 100;

/** Shown when the all-watchlists read fails (first page or a later one). */
const TARGETS_READ_ERROR = "Couldn't load these targets.";

/** The all-watchlists pool: no ranked group, nothing to reorder. */
const NO_ORDER: SlotOrderController = {
  rows: [],
  status: "idle",
  canReorder: false,
  reorder: () => undefined,
};

/**
 * The rows of a supplied order that match the search (name or club),
 * case-insensitively — the scenario's group is already complete on the
 * client, so nothing is re-read.
 * @param rows - The controller's rows
 * @param search - Trimmed search text
 * @returns The matching rows, in order
 */
const filterRows = (
  rows: readonly SlotOrderRow[],
  search: string
): readonly SlotOrderRow[] => {
  if (!search) return rows;
  const needle = search.toLowerCase();
  return rows.filter(
    row =>
      row.displayName.toLowerCase().includes(needle) ||
      (row.club ?? "").toLowerCase().includes(needle)
  );
};

/**
 * SE-8427: what opened the pop-out, for its copy. A widget-originated look
 * ("View N targets", `readOnly`) or the unranked all-watchlists pool is a
 * `targets` opening; a supplied order is always a `group` opening, `readOnly`
 * there only meaning a viewer.
 * @param flags - The opening's source flags
 * @param flags.readOnly - Whether the opening never enables reordering
 * @param flags.allWatchlists - Whether the rows come from every list
 * @param flags.supplied - Whether the opener supplied the order
 * @returns The opening the View renders copy for
 */
const resolveOpening = ({
  readOnly,
  allWatchlists,
  supplied,
}: {
  readonly readOnly: boolean;
  readonly allWatchlists: boolean;
  readonly supplied: boolean;
}): PositionPopoutOpening =>
  (readOnly || allWatchlists) && !supplied ? "targets" : "group";

/** Public props for the position pop-out. */
export interface WatchlistPositionPopoutProps {
  readonly watchlistId: string;
  readonly slot: TacticalSlot;
  readonly isOpen: boolean;
  readonly preferenceOrder?: WatchlistPreferenceOrderController;
  /**
   * SE-8393: the group's rows and reorder from whoever owns the order —
   * the scenario slot pop-out. When given, no list read happens, the
   * search filters on the client and the group is complete by definition.
   */
  readonly order?: SlotOrderController;
  /** SE-8393: the opener's copy (title, placeholder, legend). */
  readonly copy?: PositionPopoutCopy;
  /** SE-8393: the pop-out's own root test id; default `watchlist-position-popout`. */
  readonly testID?: string;
  /** SE-8393: the D5 save-status line under the title, when the opener owns one. */
  readonly saveStatus?: PitchSaveStatusLineProps;
  /**
   * PV10: where the rows come from. `list` (default) is this list's ranked
   * group; `all-watchlists` is the distinct targets of every accessible list
   * whose natural position maps onto the slot — read-only, no ranks.
   */
  readonly targetPool?: WeakSlotTargetPool;
  /**
   * PV10: a widget-originated opening ("View N targets") is a read-only
   * look at the group even on the list pool; reordering stays with the
   * pitch's own group opening.
   */
  readonly readOnly?: boolean;
  readonly onClose: () => void;
  readonly onOpenInTable: (slot: TacticalSlot, search: string) => void;
  /**
   * PV22 (SE-8372): a per-row action — the scenario's blue + — rendered
   * when given; the watchlist pop-out never gives one. Whoever mounts it
   * decides editability (a viewer gets none).
   */
  readonly rowAction?: PositionPopoutRowAction;
  /**
   * PV22: whether the footer's Table hand-off renders on the all-watchlists
   * pool too (the scenario's Table takes a position filter). Default: the
   * list pool only.
   */
  readonly showOpenInTable?: boolean;
  /**
   * SE-8535: the ● squad players at the slot, listed read-only above the
   * targets on the watchlist's card opening. Filtered by the same search.
   */
  readonly squadRows?: readonly SlotOrderRow[];
  /**
   * SE-8600: the sporting filter the field behind is drawn with. The group
   * read applies it too, so a slot's pop-out never lists members the field
   * (and the Table it hands off to) filtered out.
   */
  readonly filter?: WatchlistPitchFilterInput;
}

/**
 * Pages through the whole group, projects saved ranks and writes reorders
 * through the shared revision queue.
 * @param props - Slot identity, controller and hand-off callbacks.
 * @param props.watchlistId - Open watchlist identity.
 * @param props.slot - Tactical slot whose group is shown.
 * @param props.isOpen - Whether the pop-out is open.
 * @param props.preferenceOrder - Shared preference-order controller.
 * @param props.order - SE-8393: a supplied order source, when the opener owns the rows.
 * @param props.copy - SE-8393: the opener's copy.
 * @param props.testID - SE-8393: the root test id.
 * @param props.saveStatus - SE-8393: the save-status line, when the opener owns one.
 * @param props.targetPool - Row source: this list, or all watchlists.
 * @param props.readOnly - Whether this opening never enables reordering.
 * @param props.onClose - Closes the pop-out.
 * @param props.onOpenInTable - Opens the Table filtered to this position.
 * @param props.rowAction - PV22: the + per row, when offered.
 * @param props.showOpenInTable - PV22: force the Table hand-off on any pool.
 * @param props.squadRows - SE-8535: the slot's ● squad rows, when listed.
 * @param props.filter - SE-8600: the field's sporting filter, applied to the group read.
 */
const WatchlistPositionPopoutContainer = ({
  watchlistId,
  slot,
  isOpen,
  preferenceOrder,
  order,
  copy,
  testID,
  saveStatus,
  targetPool = "list",
  readOnly = false,
  onClose,
  onOpenInTable,
  rowAction,
  showOpenInTable,
  squadRows,
  filter,
}: WatchlistPositionPopoutProps) => {
  const [search, setSearch] = useState("");
  const [pageError, setPageError] = useState<string | null>(null);
  const [retryTick, setRetryTick] = useState(0);
  const trimmedSearch = search.trim();
  const visibleSquadRows = useMemo(
    () => (squadRows ? filterRows(squadRows, trimmedSearch) : undefined),
    [squadRows, trimmedSearch]
  );
  const allWatchlists = targetPool === "all-watchlists";
  const supplied = order !== undefined;
  const { data, fetchMore, loading } = useScoutingGetWatchlistPitchQuery({
    variables: {
      id: watchlistId,
      first: GROUP_PAGE_SIZE,
      filter: {
        ...filter,
        tacticalSlots: [slot],
        ...(trimmedSearch ? { search: trimmedSearch } : {}),
      },
    },
    skip: !isOpen || allWatchlists || supplied,
    notifyOnNetworkStatusChange: true,
    // `no-cache`, not `network-only`: the latter still WRITES the slot group
    // into `WatchlistPitch.members` — one field per list — and replaces the
    // page the field behind the pop-out renders (same class as the SE-8337
    // regression; the preference-order controller made the same choice).
    fetchPolicy: "no-cache",
  });
  const pitch = data?.getWatchlistPitch;
  /**
   * The query this pop-out is paging: a slot, search or filter change keeps the
   * container mounted but starts a new connection, so the cursor guard and
   * the page error are scoped to this key and a superseded page completion
   * never writes state for the query that replaced it.
   */
  const queryKey = `${watchlistId}|${slot}|${trimmedSearch}|${JSON.stringify(filter ?? {})}`;
  const requestedCursor = useRef<{
    readonly key: string;
    readonly after: string;
  } | null>(null);
  // Reset the page error when the query changes (adjust-during-render, no
  // effect): the new query starts on its first page and pages on its own.
  const [errorQueryKey, setErrorQueryKey] = useState(queryKey);
  if (errorQueryKey !== queryKey) {
    setErrorQueryKey(queryKey);
    setPageError(null);
  }
  const listComplete = pitch ? !pitch.members.pageInfo.hasNextPage : false;
  // The order source: the opener's controller, else this list's loaded group
  // (the all-watchlists pool has neither: read-only rows by natural position).
  const listOrder = useMemo<SlotOrderController>(
    () =>
      order ??
      (allWatchlists
        ? NO_ORDER
        : watchlistSlotOrder(
            pitch?.members.edges.map(edge => edge.node) ?? [],
            preferenceOrder,
            listComplete
          )),
    [allWatchlists, listComplete, order, pitch, preferenceOrder]
  );
  const listRows = useMemo(
    () =>
      supplied ? filterRows(listOrder.rows, trimmedSearch) : listOrder.rows,
    [listOrder.rows, supplied, trimmedSearch]
  );
  const { idsFor } = useSlotNaturalPositionIds(!isOpen || !allWatchlists);
  const positionIds = useMemo(
    () => (allWatchlists ? idsFor(slot) : []),
    [allWatchlists, idsFor, slot]
  );
  const targets = usePositionTargetRows({
    isOpen: isOpen && allWatchlists,
    positionIds,
    search: trimmedSearch,
  });
  const rows = allWatchlists ? targets.rows : listRows;
  const playerIds = useMemo(() => rows.map(row => row.playerId), [rows]);
  const playerNames = useMemo(
    () => Object.fromEntries(rows.map(row => [row.playerId, row.displayName])),
    [rows]
  );
  const canEdit = !allWatchlists && !readOnly && listOrder.canReorder;
  const { reorder } = listOrder;
  const handleMove = useCallback(
    (playerId: string, destination: WatchlistMoveDestination) => {
      void reorder(moveVisiblePlayer(playerIds, playerId, destination));
    },
    [playerIds, reorder]
  );
  const handleReorder = useCallback(
    (orderedPlayerIds: readonly string[]) => {
      void reorder(orderedPlayerIds);
    },
    [reorder]
  );
  const handleClearSearch = useCallback(() => setSearch(""), []);
  const handleOpenInTable = useCallback(
    () => onOpenInTable(slot, trimmedSearch),
    [onOpenInTable, slot, trimmedSearch]
  );

  const handleRetryPage = useCallback(() => {
    requestedCursor.current = null;
    setPageError(null);
    setRetryTick(tick => tick + 1);
  }, []);
  const pageInfo = pitch?.members.pageInfo;
  const isLoading = allWatchlists
    ? targets.loading
    : loading || (Boolean(pitch) && !listComplete && !pageError);
  // The pool decides which read's failure the pop-out reports.
  const targetsPageError = targets.error ? TARGETS_READ_ERROR : null;
  const shownPageError = allWatchlists ? targetsPageError : pageError;
  // A new query (slot or search change, including clearing a search) starts
  // paging from scratch: forget the cursor the previous query had requested,
  // otherwise its first page would look already-fetched and paging would stop.
  // A layout effect, so the reset lands in the same commit as the new query:
  // a page completing between the commit and a passive effect would still
  // pass the previous request's identity check.
  useLayoutEffect(() => {
    requestedCursor.current = null;
  }, [queryKey]);
  useEffect(() => {
    const after = pageInfo?.endCursor;
    if (!isOpen || !pageInfo?.hasNextPage || !after || pageError) return;
    const requested = requestedCursor.current;
    if (requested?.key === queryKey && requested.after === after) return;
    requestedCursor.current = { key: queryKey, after };
    const isCurrent = () =>
      requestedCursor.current?.key === queryKey &&
      requestedCursor.current.after === after;
    void fetchMore({
      variables: { after },
      // Apollo merges the page into the observable's CURRENT variables, so a
      // page that completes after the slot or search changed would land in
      // the query that replaced it: drop it unless this request is still
      // the one being paged.
      updateQuery: (previous, { fetchMoreResult }) =>
        isCurrent() ? appendPitchPage(previous, fetchMoreResult) : previous,
    })
      .then(() => {
        if (isCurrent()) setPageError(null);
      })
      .catch(() => {
        if (!isCurrent()) return;
        requestedCursor.current = null;
        setPageError("Couldn't load the rest of this group.");
      });
  }, [fetchMore, isOpen, pageError, pageInfo, queryKey, retryTick]);

  return (
    <WatchlistPositionPopoutView
      slot={slot}
      isOpen={isOpen}
      rows={rows}
      squadRows={visibleSquadRows}
      squadTotal={squadRows?.length}
      totalCount={
        allWatchlists
          ? targets.totalCount
          : supplied
            ? listOrder.rows.length
            : rows.length
      }
      opening={resolveOpening({ readOnly, allWatchlists, supplied })}
      copy={copy}
      testID={testID}
      saveStatus={saveStatus}
      showOpenInTable={showOpenInTable ?? !allWatchlists}
      search={search}
      isLoading={isLoading}
      pageError={shownPageError}
      onRetryPage={allWatchlists ? targets.retry : handleRetryPage}
      canEdit={canEdit}
      playerNames={playerNames}
      onClose={onClose}
      onSearchChange={setSearch}
      onClearSearch={handleClearSearch}
      onMove={handleMove}
      onReorder={handleReorder}
      onOpenInTable={handleOpenInTable}
      rowAction={rowAction}
    />
  );
};

WatchlistPositionPopoutContainer.displayName =
  "WatchlistPositionPopoutContainer";
export default memo(WatchlistPositionPopoutContainer);
